import React from 'react';
import { 
  Calendar, Plus, ArrowDownLeft, X, Loader2 
} from 'lucide-react';
import { updateDoc, doc, serverTimestamp } from '../firebase';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { cn } from '../lib/utils';

interface BudgetVarianceProps {
  accounts: any[];
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  selectedBudgetId: string | null;
  setSelectedBudgetId: (id: string) => void;
  budgets: any[];
  setIsBudgetModalOpen: (val: boolean) => void;
  isBudgetModalOpen: boolean;
  trackingCategories: any[];
  activeBudget: any;
  budgetStartDate: string;
  setBudgetStartDate: (date: string) => void;
  budgetPeriod: number;
  setBudgetPeriod: (period: number) => void;
  budgetMonths: string[];
  budgetValues: Record<string, number>;
  invoices: any[];
  bills: any[];
  manualJournals: any[];
  newBudgetName: string;
  setNewBudgetName: (name: string) => void;
  newBudgetTrackingSelections: Record<string, string>;
  setNewBudgetTrackingSelections: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  handleCreateBudget: () => void;
  isSavingBudget: boolean;
  setActiveReport: (report: string | null) => void;
}

interface NormalizedBudgetVarianceReport {
  budget: any | null;
  actuals: Record<string, number>;
  budgetValues: Record<string, number>;
  variances: Record<string, number>;
  variancePercentages: Record<string, number>;
}

const toAmount = (value: any): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : undefined;
  }
  return undefined;
};

const normalizeAmountMap = (source: any, fields: string[]): Record<string, number> => {
  const normalized: Record<string, number> = {};
  const collection = source && typeof source === 'object' && !Array.isArray(source)
    ? source.accounts ?? source.items ?? source.lines ?? source.values ?? source
    : source;

  const addEntry = (fallbackKey: string, entry: any) => {
    const key = entry && typeof entry === 'object'
      ? entry.accountCode ?? entry.code ?? entry.account?.code ?? entry.accountId ?? entry.account?.id ?? entry.accountName ?? entry.account?.name ?? fallbackKey
      : fallbackKey;
    const rawAmount = entry && typeof entry === 'object'
      ? fields.map(field => entry[field]).find(value => toAmount(value) !== undefined)
      : entry;
    const amount = toAmount(rawAmount);

    if (key !== undefined && key !== null && amount !== undefined) {
      const accountKey = String(key);
      normalized[accountKey] = (normalized[accountKey] || 0) + amount;
    }
  };

  if (Array.isArray(collection)) {
    collection.forEach(entry => addEntry('', entry));
  } else if (collection && typeof collection === 'object') {
    Object.entries(collection).forEach(([key, entry]) => addEntry(key, entry));
  }

  return normalized;
};

const normalizeBudgetVarianceReport = (payload: any): NormalizedBudgetVarianceReport => {
  const report = payload?.data ?? payload ?? {};
  const rows = report.rows ?? report.accounts ?? report.lineItems;
  const withRowsFallback = (source: any, fields: string[]) => {
    const values = normalizeAmountMap(source, fields);
    return Object.keys(values).length > 0 || !rows ? values : normalizeAmountMap(rows, fields);
  };

  return {
    budget: report.budgetDetails ?? report.budget?.details ?? (Array.isArray(report.budget) ? null : report.budget) ?? null,
    actuals: withRowsFallback(report.actuals ?? report.actual, ['actual', 'actuals', 'actualAmount', 'amount', 'value', 'total']),
    budgetValues: withRowsFallback(report.budgetValues ?? report.budget?.values ?? report.budget, ['budget', 'budgeted', 'budgetAmount', 'amount', 'value', 'total']),
    variances: withRowsFallback(report.variance ?? report.variances, ['variance', 'varianceAmount', 'amount', 'value', 'total']),
    variancePercentages: withRowsFallback(report.variancePercentages ?? report.variance?.percentages ?? report.variance, ['variancePercent', 'variancePercentage', 'percentage', 'percent'])
  };
};

const reportValueForAccount = (values: Record<string, number>, account: any): number | undefined => {
  return values[account.code] ?? values[account.id] ?? values[account.name];
};

export const BudgetVariance = ({
  accounts, startDate, setStartDate, endDate, setEndDate,
  selectedBudgetId, setSelectedBudgetId, budgets,
  setIsBudgetModalOpen, isBudgetModalOpen, trackingCategories,
  activeBudget, budgetStartDate, setBudgetStartDate, budgetPeriod, setBudgetPeriod,
  budgetMonths, budgetValues, invoices, bills, manualJournals,
  newBudgetName, setNewBudgetName, newBudgetTrackingSelections, setNewBudgetTrackingSelections,
  handleCreateBudget, isSavingBudget, setActiveReport
}: BudgetVarianceProps) => {
  const [apiReport, setApiReport] = React.useState<NormalizedBudgetVarianceReport | null>(null);
  const appAuthToken = typeof window === 'undefined' ? null : window.localStorage.getItem('app_auth_token');
  const orgId = typeof window === 'undefined'
    ? null
    : window.localStorage.getItem('selected_org_id') || window.localStorage.getItem('orgId') || window.localStorage.getItem('organizationId');

  React.useEffect(() => {
    if (!selectedBudgetId || !appAuthToken || !orgId) {
      setApiReport(null);
      return;
    }

    const controller = new AbortController();
    const loadReport = async () => {
      try {
        const response = await fetch(
          `/api/organizations/${encodeURIComponent(orgId)}/reports/budget-variance?budgetId=${encodeURIComponent(selectedBudgetId)}`,
          {
            headers: {
              Authorization: `Bearer ${appAuthToken}`,
              Accept: 'application/json'
            },
            signal: controller.signal
          }
        );

        if (!response.ok) throw new Error(`Budget variance request failed: ${response.status}`);
        setApiReport(normalizeBudgetVarianceReport(await response.json()));
      } catch (error) {
        if ((error as { name?: string }).name !== 'AbortError') setApiReport(null);
      }
    };

    setApiReport(null);
    void loadReport();
    return () => controller.abort();
  }, [selectedBudgetId, appAuthToken, orgId]);

  const isWithinRange = (date: string) => {
    return date >= startDate && date <= endDate;
  };

  // Calculate actuals for the period
  const actuals: Record<string, number> = {};
  
  // Invoices
  invoices.forEach(inv => {
    if (isWithinRange(inv.date) && inv.status !== 'Draft' && inv.status !== 'Void') {
      (inv.items || []).forEach((item: any) => {
        const account = accounts.find(a => a.id === item.accountId || a.code === item.accountId || a.name === item.accountName);
        if (account) {
          const amount = item.total || (item.quantity * item.unitPrice);
          actuals[account.code] = (actuals[account.code] || 0) + amount;
        }
      });
    }
  });

  // Bills
  bills.forEach(bill => {
    if (isWithinRange(bill.date) && bill.status !== 'Draft' && bill.status !== 'Void') {
      (bill.items || []).forEach((item: any) => {
        const account = accounts.find(a => a.id === item.accountId || a.code === item.accountId || a.name === item.accountName);
        if (account) {
          const amount = item.total || (item.quantity * item.unitPrice);
          actuals[account.code] = (actuals[account.code] || 0) + amount;
        }
      });
    }
  });

  // Manual Journals
  manualJournals.forEach(journal => {
    if (isWithinRange(journal.date)) {
      (journal.lines || []).forEach((line: any) => {
        const account = accounts.find(a => a.id === line.accountId || a.code === line.accountId);
        if (account) {
          const amount = (line.debit || 0) - (line.credit || 0);
          actuals[account.code] = (actuals[account.code] || 0) + Math.abs(amount);
        }
      });
    }
  });

  const groupedAccounts = {
    'Revenue': accounts.filter(a => a.type === 'Sales' || a.type === 'Other Income' || a.category === 'Revenue'),
    'Cost of Sales': accounts.filter(a => a.type === 'Direct Cost'),
    'Operating Expenses': accounts.filter(a => a.type === 'Expense' || (a.category === 'Expense' && a.type !== 'Direct Cost')),
    'Assets': accounts.filter(a => ['Bank', 'Current Asset', 'Fixed Asset', 'Non-current Asset'].includes(a.type)),
    'Liabilities': accounts.filter(a => ['Current Liability', 'Non-current Liability'].includes(a.type)),
    'Equity': accounts.filter(a => a.type === 'Equity')
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Budget</span>
            <select 
              value={selectedBudgetId || ''}
              onChange={(e) => {
                if (e.target.value === 'new') setIsBudgetModalOpen(true);
                else setSelectedBudgetId(e.target.value);
              }}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer min-w-[120px]"
            >
              <option value="" disabled>Select Budget</option>
              {budgets.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
              <option value="new">+ Add new budget</option>
            </select>
          </div>

          <button
            onClick={() => setIsBudgetModalOpen(true)}
            className="p-2 bg-[#141414] text-[#E4E3E0] rounded-full hover:scale-110 transition-transform shadow-lg"
            title="Create New Budget"
          >
            <Plus className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Start</span>
            <input 
              type="month" 
              value={budgetStartDate}
              onChange={async (e) => {
                const newDate = e.target.value;
                setBudgetStartDate(newDate);
                if (selectedBudgetId) {
                  try {
                    if (appAuthToken && orgId) {
                      await fetch(`/api/organizations/${encodeURIComponent(orgId)}/budgets/${encodeURIComponent(selectedBudgetId)}`, {
                        method: 'PATCH',
                        headers: { Authorization: `Bearer ${appAuthToken}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ startDate: newDate }),
                      });
                    } else {
                      await updateDoc(doc(db, 'budgets', selectedBudgetId), {
                        startDate: newDate,
                        updatedAt: serverTimestamp()
                      });
                    }
                  } catch (error) {
                    handleFirestoreError(error, OperationType.UPDATE, 'budgets');
                  }
                }
              }}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Period</span>
            <select 
              value={budgetPeriod}
              onChange={async (e) => {
                const newPeriod = Number(e.target.value);
                setBudgetPeriod(newPeriod);
                if (selectedBudgetId) {
                  try {
                    if (appAuthToken && orgId) {
                      await fetch(`/api/organizations/${encodeURIComponent(orgId)}/budgets/${encodeURIComponent(selectedBudgetId)}`, {
                        method: 'PATCH',
                        headers: { Authorization: `Bearer ${appAuthToken}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ period: newPeriod }),
                      });
                    } else {
                      await updateDoc(doc(db, 'budgets', selectedBudgetId), {
                        period: newPeriod,
                        updatedAt: serverTimestamp()
                      });
                    }
                  } catch (error) {
                    handleFirestoreError(error, OperationType.UPDATE, 'budgets');
                  }
                }
              }}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value={3}>3 Months</option>
              <option value={6}>6 Months</option>
              <option value={12}>12 Months</option>
              <option value={24}>24 Months</option>
            </select>
          </div>

          {trackingCategories.map(category => (
            <div key={category.id} className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">{category.name}</span>
              <select 
                value={activeBudget?.trackingSelections?.[category.id] || ''}
                disabled={!activeBudget}
                onChange={async (e) => {
                  if (!selectedBudgetId) return;
                  try {
                    if (appAuthToken && orgId) {
                      const currentSelections = { ...(activeBudget?.trackingSelections || {}), [category.id]: e.target.value };
                      await fetch(`/api/organizations/${encodeURIComponent(orgId)}/budgets/${encodeURIComponent(selectedBudgetId)}`, {
                        method: 'PATCH',
                        headers: { Authorization: `Bearer ${appAuthToken}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ trackingSelections: currentSelections }),
                      });
                    } else {
                      await updateDoc(doc(db, 'budgets', selectedBudgetId), {
                        [`trackingSelections.${category.id}`]: e.target.value,
                        updatedAt: serverTimestamp()
                      });
                    }
                  } catch (error) {
                    handleFirestoreError(error, OperationType.UPDATE, 'budgets');
                  }
                }}
                className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
              >
                <option value="">All {category.name}</option>
                {category.options?.map((option: string) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card/50 border rounded-[40px] p-4 sm:p-8 lg:p-12 shadow-sm">
        <div className="text-center mb-6 sm:mb-12">
          <h1 className="fluid-h1 font-bold tracking-tighter italic serif mb-2">Budget Variance</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            {(apiReport?.budget?.name || activeBudget?.name)} • {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
          </p>
        </div>

        <div className="table-wrap">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                <th className="p-4 text-start text-[10px] uppercase font-bold tracking-widest opacity-40">Account</th>
                <th className="p-4 text-end text-[10px] uppercase font-bold tracking-widest opacity-40">Actual</th>
                <th className="p-4 text-end text-[10px] uppercase font-bold tracking-widest opacity-40">Budget</th>
                <th className="p-4 text-end text-[10px] uppercase font-bold tracking-widest opacity-40">Variance</th>
                <th className="p-4 text-end text-[10px] uppercase font-bold tracking-widest opacity-40">Var %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              {Object.entries(groupedAccounts).map(([category, categoryAccounts]) => (
                <React.Fragment key={category}>
                  <tr className="bg-[#141414]/5">
                    <td colSpan={5} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">{category}</td>
                  </tr>
                  {categoryAccounts.map(account => {
                    const fallbackActual = actuals[account.code] || 0;
                    // Sum Firebase budget values for the selected range when no API value is available.
                    const fallbackBudget = budgetMonths.reduce((sum, month) => {
                      if (month >= startDate.substring(0, 7) && month <= endDate.substring(0, 7)) {
                        return sum + (budgetValues[`${account.code}_${month}`] || 0);
                      }
                      return sum;
                    }, 0);
                    const actual = apiReport ? reportValueForAccount(apiReport.actuals, account) ?? fallbackActual : fallbackActual;
                    const budget = apiReport ? reportValueForAccount(apiReport.budgetValues, account) ?? fallbackBudget : fallbackBudget;

                    if (actual === 0 && budget === 0) return null;

                    const variance = apiReport ? reportValueForAccount(apiReport.variances, account) ?? (actual - budget) : actual - budget;
                    const varPercent = apiReport
                      ? reportValueForAccount(apiReport.variancePercentages, account) ?? (budget !== 0 ? (variance / budget) * 100 : 0)
                      : (budget !== 0 ? (variance / budget) * 100 : 0);

                    return (
                      <tr key={account.id} className="hover:bg-[#141414]/5 transition-colors">
                        <td className="p-4 text-xs font-bold">{account.name}</td>
                        <td className="p-4 text-right font-mono text-xs">{actual.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className="p-4 text-right font-mono text-xs">{budget.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className={cn(
                          "p-4 text-right font-mono text-xs font-bold",
                          variance > 0 ? "text-green-600" : variance < 0 ? "text-red-600" : ""
                        )}>
                          {variance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className={cn(
                          "p-4 text-right font-mono text-xs",
                          varPercent > 0 ? "text-green-600" : varPercent < 0 ? "text-red-600" : ""
                        )}>
                          {varPercent.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isBudgetModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsBudgetModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border-2 border-[#141414] rounded-3xl shadow-[16px_16px_0px_0px_rgba(20,20,20,1)] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
            <div className="px-8 py-6 border-b-2 border-[#141414] flex justify-between items-center">
              <h2 className="text-2xl font-bold italic serif">New Budget</h2>
              <button onClick={() => setIsBudgetModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest opacity-70 mb-2">Budget Name</label>
                <input 
                  type="text"
                  value={newBudgetName}
                  onChange={(e) => setNewBudgetName(e.target.value)}
                  placeholder="e.g. 2026 Growth Plan"
                  className="w-full px-4 py-3 bg-white/50 border-2 border-[#141414] rounded-xl font-bold focus:outline-none focus:ring-4 focus:ring-[#141414]/5 transition-all"
                />
              </div>
              {trackingCategories.map(category => (
                <div key={category.id}>
                  <label className="block text-[10px] font-bold uppercase tracking-widest opacity-70 mb-2">{category.name}</label>
                  <select 
                    value={newBudgetTrackingSelections[category.id] || ''}
                    onChange={(e) => setNewBudgetTrackingSelections(prev => ({
                      ...prev,
                      [category.id]: e.target.value
                    }))}
                    className="w-full px-4 py-3 bg-white/50 border-2 border-[#141414] rounded-xl font-bold focus:outline-none focus:ring-4 focus:ring-[#141414]/5 transition-all"
                  >
                    <option value="">All {category.name}</option>
                    {category.options?.map((option: string) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              ))}
              <button 
                onClick={handleCreateBudget}
                disabled={!newBudgetName.trim() || isSavingBudget}
                className="w-full py-4 bg-[#141414] text-[#E4E3E0] rounded-full font-bold text-xs uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSavingBudget && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Budget
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
